/**
 * Analytics Helper Utilities
 * Provides helper functions for analytics tracking
 */

/**
 * SHA-256 hash function for privacy-compliant tracking
 * @param {string} message - String to hash
 * @returns {Promise<string>} Hex string of hash
 */
export async function sha256(message) {
  // Use Web Crypto API if available
  if (window.crypto && window.crypto.subtle) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // Fallback to simple hash for older browsers
  let hash = 0;
  for (let i = 0; i < message.length; i++) {
    const char = message.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Get email domain from email address
 * @param {string} email - Email address
 * @returns {string} Domain part of email
 */
export function getEmailDomain(email) {
  if (!email || !email.includes('@')) {
    return 'unknown';
  }
  return email.split('@')[1].toLowerCase();
}

/**
 * Parse and validate UTM parameters
 * @param {URLSearchParams} params - URL search params
 * @returns {Object} Valid UTM parameters
 */
export function parseUTMParams(params) {
  const validParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const utmParams = {};

  validParams.forEach(param => {
    const value = params.get(param);
    if (value && value.length <= 100) { // Limit length for security
      utmParams[param] = value;
    }
  });

  return utmParams;
}

/**
 * Get referrer information
 * @returns {Object} Referrer data
 */
export function getReferrerInfo() {
  const referrer = document.referrer;
  if (!referrer) {
    return { referrer: 'direct', referrer_domain: 'direct' };
  }

  try {
    const url = new URL(referrer);
    return {
      referrer: referrer.slice(0, 200), // Truncate for privacy
      referrer_domain: url.hostname
    };
  } catch {
    return { referrer: 'invalid', referrer_domain: 'unknown' };
  }
}

/**
 * Determine error type from error object
 * @param {Error|Object} error - Error object
 * @returns {string} Error type
 */
export function getErrorType(error) {
  if (!error) return 'unknown';

  if (error.isNetworkError || error.message?.includes('network')) {
    return 'network';
  }

  if (error.status === 400 || error.message?.includes('validation')) {
    return 'validation';
  }

  if (error.status >= 500) {
    return 'server';
  }

  return 'unknown';
}

/**
 * Calculate completion percentage
 * @param {number} completed - Number of completed items
 * @param {number} total - Total number of items
 * @returns {number} Percentage (0-100)
 */
export function calculateCompletionRate(completed, total) {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

/**
 * Format time duration for analytics
 * @param {number} milliseconds - Time in milliseconds
 * @returns {number} Time in seconds (rounded)
 */
export function formatDuration(milliseconds) {
  return Math.round(milliseconds / 1000);
}

/**
 * Check if Do Not Track is enabled
 * @returns {boolean} True if DNT is enabled
 */
export function isDNTEnabled() {
  return (
    navigator.doNotTrack === '1' ||
    window.doNotTrack === '1' ||
    navigator.msDoNotTrack === '1'
  );
}

/**
 * Check if user has given consent for analytics
 * @returns {boolean} True if consent given or no consent system
 */
export function hasAnalyticsConsent() {
  // Check for DNT first
  if (isDNTEnabled()) {
    return false;
  }

  // Check for cookie consent (if implemented)
  const consent = localStorage.getItem('analytics_consent');

  // If no consent system implemented, assume consent
  if (consent === null) {
    return true;
  }

  return consent === 'granted';
}

/**
 * Store analytics consent
 * @param {boolean} granted - Whether consent is granted
 */
export function setAnalyticsConsent(granted) {
  localStorage.setItem('analytics_consent', granted ? 'granted' : 'denied');

  // Reload analytics with new consent state
  if (window.analytics) {
    window.analytics.updateConsent(granted);
  }
}

/**
 * Debounce function for analytics events
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Generate session ID
 * @returns {string} Session ID
 */
export function generateSessionId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Get or create session ID
 * @returns {string} Session ID
 */
export function getSessionId() {
  let sessionId = sessionStorage.getItem('analytics_session_id');

  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('analytics_session_id', sessionId);
  }

  return sessionId;
}